<template>
  <div class="dashboard-container p-4 min-h-[100vh]">
    <!-- Screenshot Header (only visible during screenshot capture) -->
    <div class="screenshot-header" :class="{ hidden: !showScreenshotHeader }">
      <div class="maze-pattern">
        <div class="header-content">
          <div class="workspace-info">
            <img
              v-if="currentWorkspaceLogo"
              :src="currentWorkspaceLogo"
              alt="Workspace Logo"
              class="workspace-logo"
            />
            <span class="workspace-name">{{
              currentWorkspaceName || "Workspace"
            }}</span>
          </div>
          <div class="date-text">{{ currentDate }}</div>
        </div>
      </div>
    </div>

    <!-- Date Navigation Moved to Top -->
    <div class="w-full mb-6">
      <div v-if="availableRankTrackerDates.length > 0" class="date-navigation">
        <div class="flex items-center">
          <v-btn
            icon
            small
            @click="scrollDates('left')"
            :disabled="scrollPosition <= 0"
            title="Scroll left"
            class="flex-shrink-0"
          >
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>

          <div class="dates-container overflow-x-hidden mx-2 flex-grow">
            <div
              class="dates-scroll flex transition-transform duration-300"
              :style="{ transform: `translateX(-${scrollPosition}px)` }"
            >
              <div
                v-for="(date, index) in availableRankTrackerDates"
                :key="index"
                @click="selectDate(date.date, index)"
                class="date-item px-3 py-2 cursor-pointer whitespace-nowrap mx-1 rounded-md transition-colors"
                :class="{
                  'bg-primary text-white font-medium':
                    selectedDateIndex === index,
                  'hover:bg-gray-100': selectedDateIndex !== index,
                }"
              >
                {{ formatDisplayDate(date.date) }}
              </div>
            </div>
          </div>

          <v-btn
            icon
            small
            @click="scrollDates('right')"
            :disabled="isScrollEnd"
            title="Scroll right"
            class="flex-shrink-0"
          >
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Stats Overview -->
    <div class="stats-overview flex flex-col gap-4 mb-2">
      <div
        v-if="!showScreenshotHeader"
        class="flex justify-between items-center"
      >
        <!-- <h2 class="text-xl font-semibold">Competitor Analysis Overview</h2> -->
        <div class="flex justify-between items-center gap-2">
          <div class="w-full relative">
            <v-btn @click="toggleStoreDropdown" outlined color="primary">
              {{ selectedWIDs?.length || 0 }} Selected Locations
            </v-btn>
            <LocationDropdown
              class="-mt-4"
              ref="locationDropdown"
              :stores="filteredStores"
              :showStoreDropdown="showStoreDropdown"
              :show-store-dropdown="showStoreDropdown"
              v-model="localSelectedWIDs"
              @change="handleLocationChange"
            />
          </div>
          <div class="flex gap-2">
            <v-btn
              color="primary"
              icon
              @click="handleScreenshotCapture"
              title="Save dashboard as image"
            >
              <v-icon>mdi-download-box-outline</v-icon>
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Overview Stats Cards -->
      <div
        class="grid grid-cols-1 gap-4 my-4"
        :class="{ 'md:grid-cols-2': selectedWIDs.length >= 1 }"
        v-if="selectedWIDs.length > 0 && displayedWorkspaces.length > 0"
      >
        <div
          class="stat-card bg-white rounded-lg shadow-md p-5 border-l-4 border-primary transition-all hover:shadow-lg"
        >
          <div class="text-gray-600 mb-1 font-medium">Overall Average Rank</div>
          <div class="text-3xl font-bold text-primary">
            {{ calculateOverallRank() }}
          </div>
          <div class="mt-2 text-sm text-gray-500">
            Across {{ selectedWIDs.length }} locations
          </div>
        </div>

        <div
          class="stat-card bg-white rounded-lg shadow-md p-5 border-l-4 border-indigo-500 transition-all hover:shadow-lg"
        >
          <div class="text-gray-600 mb-1 font-medium">
            Total Keywords Tracked
          </div>
          <div class="text-3xl font-bold text-indigo-500">
            {{ calculateTotalKeywords() }}
          </div>
          <div class="mt-2 text-sm text-gray-500">
            Across all selected locations
          </div>
        </div>
      </div>

      <!-- Rank Distribution -->
      <div
        class="bg-white rounded-lg shadow-md p-5 mb-4"
        v-if="selectedWIDs.length > 0 && displayedWorkspaces.length > 0"
      >
        <div class="flex justify-between items-center mb-3">
          <h3 class="text-lg font-semibold text-gray-700">Rank Distribution</h3>
          <div v-if="!showScreenshotHeader" class="relative">
            <v-select
              v-model="selectedKeywordFilter"
              :items="keywordFilterOptions"
              item-text="text"
              item-value="value"
              label="Filter by keyword"
              outlined
              dense
              class="max-w-xs"
              @change="updateRankDistribution"
            ></v-select>
          </div>
        </div>
        <div class="rank-distribution flex items-end h-32 gap-1">
          <div
            v-for="(count, index) in rankDistribution"
            :key="index"
            class="rank-bar relative flex-grow transition-all hover:opacity-90"
            :style="{
              height: `${(count / maxDistributionValue) * 100}%`,
              backgroundColor: getRankColor(index),
            }"
          >
            <div class="absolute -top-6 w-full text-center text-xs font-medium">
              {{ count || 0 }} locations
            </div>
            <div class="absolute -bottom-6 w-full text-center text-xs">
              {{ getRankLabel(index) }}
            </div>
          </div>
        </div>
      </div>

      <!-- Keyword Rankings Table -->
      <div
        class="bg-white rounded-lg shadow-md p-5 mb-4 w-full"
        v-if="
          selectedWIDs.length > 0 &&
          (displayedWorkspaces?.length > 0 || locationRanks?.length > 0)
        "
      >
        <h3 class="text-lg font-semibold mb-3 text-gray-700">
          Keyword Performance
        </h3>
        <table class="w-full border-collapse">
          <thead>
            <tr class="border-b-2 border-gray-200">
              <th class="text-left py-3 px-2 font-semibold text-gray-700">
                Keyword
              </th>
              <th
                class="text-center py-3 px-2 font-semibold text-gray-700 cursor-pointer"
                @click="sortKeywordTable('avgRank')"
              >
                Average Rank
                <v-icon small class="pb-1">
                  {{
                    keywordTableSort.column === "avgRank"
                      ? keywordTableSort.direction === "asc"
                        ? "mdi-arrow-up"
                        : "mdi-arrow-down"
                      : ""
                  }}
                </v-icon>
              </th>
              <th
                class="text-center py-3 px-2 font-semibold text-gray-700 cursor-pointer"
                @click="sortKeywordTable('count')"
              >
                Locations
                <v-icon small class="pb-1">
                  {{
                    keywordTableSort.column === "count"
                      ? keywordTableSort.direction === "asc"
                        ? "mdi-arrow-up"
                        : "mdi-arrow-down"
                      : ""
                  }}
                </v-icon>
              </th>
              <th class="text-center py-3 px-2 font-semibold text-gray-700">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="keyword in performanceKeywordRanks"
              :key="keyword.keyword"
              class="border-b hover:bg-gray-50 transition-colors"
            >
              <td class="py-3 px-2 font-medium text-left">
                {{ keyword.keyword }}
              </td>
              <td class="text-center py-3 px-2">
                <span
                  class="py-1 px-3 rounded-full font-medium"
                  :class="getRankClass(keyword.avgRank)"
                >
                  {{ formatRank(keyword.avgRank) }}
                </span>
              </td>
              <td class="text-center py-3 px-2">{{ keyword.count }}</td>
              <td class="text-center py-3 px-2">
                <!-- Removing Explore button that has no location ID -->
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Location Selector - Updated with LocationDropdown -->
      <div class="location-dropdown relative bg-white rounded-lg">
        <div
          class="flex flex-col md:flex-row items-start md:items-center gap-4"
        >
          <!-- <div class="w-full">
            <v-btn @click="toggleStoreDropdown" outlined color="primary">
              {{ selectedWIDs?.length || 0 }} Selected Locations
            </v-btn>
            <LocationDropdown
              class="-mt-4"
              ref="locationDropdown"
              :stores="filteredStores"
              :showStoreDropdown="showStoreDropdown"
              :show-store-dropdown="showStoreDropdown"
              v-model="selectedWIDs"
              @change="handleLocationChange"
            />
          </div> -->

          <!-- Month Selector -->
          <!-- <div class="w-full md:w-1/3" v-if="selectedWIDs.length > 0">
            <label class="block text-sm font-medium text-gray-700 mb-1">Select Month</label>
            <v-select
              v-model="selectedMonth"
              :items="availableMonths"
              outlined
              dense
              @change="processRankData"
            ></v-select>
          </div> -->
        </div>
      </div>

      <!-- Keyword Rankings Table -->
      <div
        class="bg-white rounded-lg shadow-md p-5 mb-4 w-full"
        v-if="selectedWIDs.length > 0 && displayedWorkspaces.length > 0"
      >
        <div class="w-full">
          <div v-if="availableDates.length > 0" class="date-navigation mb-4">
            <div class="flex items-center">
              <v-btn
                icon
                small
                @click="scrollDates2('left')"
                :disabled="scrollPosition2 <= 0"
                title="Scroll left"
                class="flex-shrink-0"
              >
                <v-icon>mdi-chevron-left</v-icon>
              </v-btn>

              <div class="dates-container-2 overflow-x-hidden mx-2 flex-grow">
                <div
                  class="dates-scroll-2 flex transition-transform duration-300"
                  :style="{ transform: `translateX(-${scrollPosition2}px)` }"
                >
                  <div
                    v-for="(date, index) in availableDates"
                    :key="index"
                    @click="selectDate(date.date, index)"
                    class="date-item px-3 py-2 cursor-pointer whitespace-nowrap mx-1 rounded-md transition-colors"
                    :class="{
                      'bg-primary text-white font-medium':
                        selectedDateIndex === index,
                      'hover:bg-gray-100': selectedDateIndex !== index,
                    }"
                  >
                    {{ formatDisplayDate(date.date) }}
                  </div>
                </div>
              </div>

              <v-btn
                icon
                small
                @click="scrollDates2('right')"
                :disabled="isScrollEnd2"
                title="Scroll right"
                class="flex-shrink-0"
              >
                <v-icon>mdi-chevron-right</v-icon>
              </v-btn>
            </div>
          </div>
        </div>
        <!-- <div class="flex justify-between items-center">
          <h3 class="text-md font-medium mb-3">Keyword Rankings</h3>

          <div v-if="availableDates.length > 0" class="date-selector">
            <v-select
              v-model="selectedDateIndex"
              :items="availableDatesOptions"
              outlined
              dense
              hide-details
              class="date-select"
              @change="handleDateChange"
            ></v-select>
          </div>
        </div> -->
        <div class="location-ranks">
          <!-- <h3 class="text-lg font-semibold mb-4">By Location</h3> -->
          <table class="w-full border-collapse">
            <thead>
              <tr class="border-b-2 border-gray-200">
                <th class="text-left py-3 px-2 font-semibold text-gray-700">
                  Location
                </th>
                <th
                  class="text-center py-3 px-2 font-semibold text-gray-700 cursor-pointer"
                  @click="sortByRank"
                >
                  Rank
                  <v-icon small class="pb-1">
                    {{
                      sortDirection
                        ? sortDirection === "asc"
                          ? "mdi-arrow-up"
                          : "mdi-arrow-down"
                        : "mdi-arrow-up"
                    }}
                  </v-icon>
                </th>
                <th class="text-center py-3 px-2 font-semibold text-gray-700">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              <template v-for="workspace in displayedWorkspaces">
                <tr
                  class="border-b hover:bg-gray-50 transition-colors"
                  :key="`workspace-${workspace.wid}`"
                >
                  <td class="py-3 px-2 text-left">
                    <div
                      class="flex items-center cursor-pointer"
                      @click="toggleLocationDetails(workspace.wid)"
                    >
                      <button
                        class="mr-2 p-1 rounded hover:bg-gray-200 transition-colors"
                        :title="
                          expandedLocations[workspace.wid]
                            ? 'Hide details'
                            : 'Show details'
                        "
                      >
                        <v-icon small>
                          {{
                            expandedLocations[workspace.wid]
                              ? "mdi-minus"
                              : "mdi-plus"
                          }}
                        </v-icon>
                      </button>
                      <div>
                        <div class="font-medium">
                          {{ getLocationName(workspace.wid).name }}
                        </div>
                        <div class="text-xs text-gray-500">
                          {{ getLocationName(workspace.wid).address }}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td class="text-center py-3 px-2">
                    <span
                      class="py-1 px-3 rounded-full font-medium"
                      :class="getRankClass(workspace.average_rank)"
                    >
                      {{ formatRank(workspace.average_rank) }}
                    </span>
                  </td>
                  <td class="text-center py-3 px-2">
                    <v-btn
                      small
                      outlined
                      color="primary"
                      @click="navigateToRankExplorer(workspace.wid, null)"
                    >
                      Explore
                      <v-icon right small>mdi-magnify</v-icon>
                    </v-btn>
                  </td>
                </tr>
                <tr
                  v-if="expandedLocations[workspace.wid]"
                  :key="`workspace-details-${workspace.wid}`"
                >
                  <td colspan="3" class="bg-gray-50 py-3 px-4">
                    <div
                      class="mb-2 p-2 border rounded"
                      v-if="workspace.ownerUpdates"
                    >
                      <h4 class="font-semibold text-sm mb-1">Owner Updates:</h4>
                      <!-- Placeholder for Owner Updates component -->
                      <div
                        v-for="(update, index) in workspace.ownerUpdates"
                        :key="index"
                        class="mb-1 p-1 border rounded text-xs"
                      >
                        <p>
                          <strong>{{ update.title }}</strong>
                        </p>
                        <p>{{ update.caption }}</p>
                        <img
                          v-if="update.imageUrl"
                          :src="update.imageUrl"
                          alt="Update image"
                          class="max-w-xs max-h-xs my-1"
                        />
                        <a
                          href="#"
                          @click.prevent="showRawData(update)"
                          class="text-xs text-blue-500 hover:underline"
                          >View Raw</a
                        >
                      </div>
                    </div>
                    <div
                      class="mb-2 p-2 border rounded"
                      v-if="workspace.profileImages"
                    >
                      <h4 class="font-semibold text-sm mb-1">
                        Profile Images:
                      </h4>
                      <div
                        v-for="(image, index) in workspace.profileImages"
                        :key="index"
                        class="mb-1 p-1 border rounded text-xs"
                      >
                        <img
                          :src="image.url"
                          :alt="image.altText || 'Profile image'"
                          class="max-w-xs max-h-xs my-1"
                        />
                        <a
                          href="#"
                          @click.prevent="showRawData(image)"
                          class="text-xs text-blue-500 hover:underline"
                          >View Raw</a
                        >
                      </div>
                    </div>
                    <table class="w-full mx-2 border-collapse keyword-table">
                      <thead>
                        <tr class="border-b border-gray-200">
                          <th
                            class="text-left py-2 text-sm font-medium text-gray-600"
                          >
                            Keyword
                          </th>
                          <th
                            class="text-center py-2 text-sm font-medium text-gray-600"
                          >
                            Rank
                          </th>
                          <th
                            class="text-center py-2 text-sm font-medium text-gray-600"
                          >
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="keyword in workspace.keywords"
                          :key="keyword.keyword"
                          class="border-b border-gray-200 hover:bg-gray-100 transition-colors"
                        >
                          <td class="py-2 text-sm text-left">
                            {{ keyword.keyword }}
                          </td>
                          <td class="text-center py-2 text-sm">
                            <span
                              class="py-1 px-2 rounded-full text-xs font-medium"
                              :class="getRankClass(keyword.rank)"
                            >
                              {{ formatRank(keyword.rank) }}
                            </span>
                          </td>
                          <td class="text-center py-2 text-sm">
                            <v-btn
                              x-small
                              text
                              color="primary"
                              @click="
                                navigateToRankExplorer(
                                  workspace.wid,
                                  keyword.keyword
                                )
                              "
                            >
                              <v-icon x-small>mdi-magnify</v-icon>
                            </v-btn>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <div
                      v-if="rawDetailsData"
                      class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50"
                    >
                      <div
                        class="bg-white p-5 rounded-lg shadow-xl max-w-2xl w-full"
                      >
                        <div class="flex justify-between items-center mb-3">
                          <h3 class="text-lg font-semibold">Raw Data</h3>
                          <button
                            @click="rawDetailsData = null"
                            class="text-gray-700 hover:text-gray-900"
                          >
                            <v-icon>mdi-close</v-icon>
                          </button>
                        </div>
                        <pre
                          class="bg-gray-100 p-3 rounded text-xs overflow-x-auto"
                          >{{ rawDetailsData }}</pre
                        >
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
      <!-- <v-text-field
          v-model="search"
          label="Search Keywords"
          outlined
          dense
          class="mb-4"
        ></v-text-field>
        <v-progress-circular
          v-if="isLoading"
          indeterminate
          color="primary"
          class="mx-auto block"
        ></v-progress-circular>
        <v-data-table
          v-else
          :headers="tableHeaders"
          :items="keywordsData"
          :items-per-page="10"
          :search="search"
          class="elevation-1"
        >
          <template v-slot:item.avgRank="{ item }">
            {{ item.avgRank.toFixed(1) }}
          </template>
          <template v-slot:item.change="{ item }">
            <div
              :class="
                item.change >= 0 ? 'text-brand-success' : 'text-brand-danger'
              "
              v-if="item.change !== null"
            >
              <v-icon small :color="item.change >= 0 ? 'success' : 'error'">
                {{ item.change >= 0 ? "mdi-arrow-up" : "mdi-arrow-down" }}
              </v-icon>
              {{ Math.abs(item.change).toFixed(1) }}
            </div>
            <div v-else>-</div>
          </template> -->
      <!-- </v-data-table> -->
    </div>

    <!-- No Keywords Found -->
    <EmptyState
      v-if="selectedWIDs.length > 0 && displayedWorkspaces.length === 0"
      message="No data available"
    />

    <!-- No Location Selected -->
    <div
      class="bg-white rounded-lg shadow-md p-5"
      v-if="selectedWIDs.length === 0"
    >
      <p class="text-gray-500 text-center">
        Please select a location to view rank tracking data.
      </p>
    </div>
  </div>
</template>

<script>
import html2canvas from "html2canvas";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/utils/firebase.utils";
import LocationDropdown from "@/components/LocationDropdown.vue";
import EmptyState from "@/components/ui/common/EmptyState.vue";

export default {
  components: {
    LocationDropdown,
    EmptyState,
  },
  props: {
    stores: {
      type: Array,
      default: () => [],
    },
    locationRanks: {
      type: Array,
      default: () => [],
    },
    locationSearch: {
      type: String,
      default: "",
    },
    selectedWIDs: {
      type: Array,
      default: () => [],
    },
    lastDateIndex: {
      type: Number,
      default: 0,
    },
    locationRanksPerformance: {
      type: Object,
      default: () => ({}),
    },
    distinctKeywordCounts: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      showScreenshotHeader: false,
      workspaceData: null,
      selectedStores: [],
      selectedMonth: null,
      availableMonths: [],
      keywordsData: [],
      isLoading: false,
      search: "",
      overallStats: {
        avgRank: 0,
        rankChange: null,
      },
      topKeyword: {},
      tableHeaders: [
        { text: "Keyword", value: "keyword", align: "start", sortable: true },
        {
          text: "Average Rank",
          value: "avgRank",
          align: "center",
          sortable: true,
        },
        { text: "Change", value: "change", align: "center", sortable: true },
        {
          text: "Locations",
          value: "locationCount",
          align: "center",
          sortable: true,
        },
      ],
      showStoreDropdown: false,
      selectedDateTab: null,
      locationHeaders: [
        { text: "Location", value: "location", sortable: true },
        { text: "Rank <Sort>", value: "rank", sortable: true },
      ],
      expandedLocations: {},
      selectedDateIndex: 0, // To select which date's data to display
      localSelectedWIDs: [], // Local copy of the prop
      scrollPosition: 0, // For first date navigation
      scrollPosition2: 0, // For second date navigation
      totalDatesWidth: 0,
      dateContainerWidth: 0,
      totalDatesWidth2: 0,
      dateContainerWidth2: 0,
      sortDirection: "asc",
      selectedKeywordFilter: "all",
      keywordFilterOptions: [
        { text: "All Keywords", value: "all" },
        // Will be populated with actual keywords
      ],
      keywordTableSort: {
        column: "avgRank",
        direction: "asc",
      },
      keywordRanks: [],
      rawDetailsData: null, // For the modal
      selectedDate: null,
      performanceKeywordRanks: [],
      localKeywordData: [], // Local copy of keyword data with counts
    };
  },
  computed: {
    currentWorkspace() {
      return this.$store.getters.currentWorkspace;
    },
    currentWorkspaceName() {
      return (
        this.workspaceData?.name || this.currentWorkspace?.name || "Workspace"
      );
    },
    currentWorkspaceLogo() {
      return (
        this.workspaceData?.logo_original ||
        this.currentWorkspace?.logo_original
      );
    },
    currentDate() {
      const now = new Date();
      return now.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
      });
    },
    isScrollEnd() {
      return (
        this.scrollPosition >= this.totalDatesWidth - this.dateContainerWidth
      );
    },
    isScrollEnd2() {
      return (
        this.scrollPosition2 >= this.totalDatesWidth2 - this.dateContainerWidth2
      );
    },
    availableRankTrackerDates() {
      // This should return the same data as availableDates but for the rank tracker section
      return this.availableDates;
    },
    availableDates() {
      if (!this.locationRanks || this.locationRanks.length === 0) {
        return [];
      }

      // Extract all dates from locationRanks array
      return this.locationRanks
        .map((rankData, index) => ({
          date: rankData.date || `Date ${index + 1}`,
          index,
        }))
        .reverse();
    },
    filteredStores() {
      if (!this.locationSearch) {
        return this.stores;
      }

      const searchTerm = this.locationSearch.toLowerCase();
      return this.stores.filter(
        (store) =>
          store.name?.toLowerCase().includes(searchTerm) ||
          store.sub_locality?.toLowerCase().includes(searchTerm) ||
          store.city?.toLowerCase().includes(searchTerm)
      );
    },
    locationOptions() {
      console.log(this.selectedWIDs, "this is the selected wids", this.stores);
      return this.stores.map((store) => {
        const locationId =
          store.gmbProfile.place_id ||
          store.gmbProfile?.name?.split("/")[1] ||
          store.gmbProfile?.name ||
          "";
        return {
          id: locationId,
          wid: store.id,
          title: store.gmbProfile.title || store.display_name || "",
          display_name: store.display_name || "",
          gmbProfile: store.gmbProfile,
          sub_locality: store.sub_locality || "",
          city: store.city || "",
          state: store.state || "Unknown",
          checked: this.selectedWIDs.includes(store.id),
          isReview: true, // This is important for the LocationDropdown component
        };
      });
    },
    locationRankData() {
      return this.locationRanks.map((rank) => ({
        ...rank,
        keywords: Object.entries(rank.ranks || {}).map(([month, data]) => ({
          ...data.keyword_ranks,
          month,
          average_rank: data.average_rank,
        })),
      }));
    },
    // Get workspaces from the selected date
    displayedWorkspaces() {
      if (!this.locationRanks.length || this.availableDates.length === 0)
        return [];
      // Ensure selectedDateIndex is within bounds of availableDates
      if (
        this.selectedDateIndex < 0 ||
        this.selectedDateIndex >= this.availableDates.length
      ) {
        return [];
      }

      // Get the selected date object from availableDates using selectedDateIndex
      const selectedAvailableDateEntry =
        this.availableDates[this.selectedDateIndex];
      if (!selectedAvailableDateEntry) {
        return [];
      }

      // Use the original index stored in the selectedAvailableDateEntry to access locationRanks
      const originalIndexInLocationRanks = selectedAvailableDateEntry.index;

      // Ensure originalIndexInLocationRanks is within bounds of locationRanks
      if (
        originalIndexInLocationRanks < 0 ||
        originalIndexInLocationRanks >= this.locationRanks.length
      ) {
        return [];
      }
      const dateData = this.locationRanks[originalIndexInLocationRanks];

      if (!dateData || !dateData.workspaces) return [];

      let workspaces = dateData.workspaces.filter(
        (workspace) =>
          this.localSelectedWIDs.length === 0 ||
          this.localSelectedWIDs.includes(workspace.wid)
      );
      if (this.sortDirection) {
        workspaces = [...workspaces].sort((a, b) => {
          const comparison = a.average_rank - b.average_rank;
          return this.sortDirection === "asc" ? comparison : -comparison;
        });
      }
      return workspaces;
    },
    rankDistribution() {
      // Return counts for rank ranges: 1-3, 4-5, 6-10, 11-20, 21+
      const distribution = [0, 0, 0, 0, 0]; // 5 rank ranges

      // Only calculate distribution when displayedWorkspaces is ready (same as location rank table)
      if (!this.displayedWorkspaces || this.displayedWorkspaces.length === 0) {
        return distribution;
      }

      // Function to add a rank to the distribution
      const addRankToDistribution = (rank) => {
        if (rank >= 1 && rank <= 3) distribution[0]++;
        else if (rank >= 4 && rank <= 5) distribution[1]++;
        else if (rank >= 6 && rank <= 10) distribution[2]++;
        else if (rank >= 11 && rank <= 20) distribution[3]++;
        else if (rank >= 21) distribution[4]++;
      };

      if (this.selectedKeywordFilter === "all") {
        // Use the exact same data as the location rank table
        this.displayedWorkspaces.forEach((ws) => {
          if (ws.average_rank != null) {
            addRankToDistribution(Math.round(ws.average_rank));
          }
        });
      } else {
        // Track unique locations for the selected keyword to avoid double counting
        const processedLocations = new Set();

        this.displayedWorkspaces.forEach((ws) => {
          if (!Array.isArray(ws.keywords)) return;

          // Find the best rank for this keyword at this location
          const keywordRanks = ws.keywords.filter(
            (k) =>
              k.keyword === this.selectedKeywordFilter ||
              k.location_variable_keyword === this.selectedKeywordFilter
          );

          if (keywordRanks.length > 0 && !processedLocations.has(ws.wid)) {
            processedLocations.add(ws.wid);
            // Use the best (lowest) rank for this keyword at this location
            const bestRank = Math.min(...keywordRanks.map((k) => k.rank));
            addRankToDistribution(bestRank);
          }
        });

        // if nothing matched the keyword filter, fall back to average rank
        if (distribution.every((c) => c === 0)) {
          this.displayedWorkspaces.forEach((ws) => {
            if (ws.average_rank != null) addRankToDistribution(ws.average_rank);
          });
        }
      }

      return distribution;
    },
    maxDistributionValue() {
      return Math.max(...this.rankDistribution, 1);
    },
    sortedKeywordRanks() {
      if (!this.keywordRanks.length) return [];

      return [...this.keywordRanks].sort((a, b) => {
        const aValue = a[this.keywordTableSort.column];
        const bValue = b[this.keywordTableSort.column];

        if (this.keywordTableSort.direction === "asc") {
          return aValue - bValue;
        } else {
          return bValue - aValue;
        }
      });
    },
  },
  mounted() {
    this.fetchWorkspaceData();
    document.addEventListener("click", this.handleClickOutside);
    if (this.stores.length > 0) {
      // Initialize with loading location data
      this.loadLocationData();
    }
    this.updateDateScrollDimensions();
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleClickOutside);
  },
  watch: {
    locationRanks: {
      handler() {
        this.loadLocationData();
        // Update scroll dimensions and scroll to latest dates when location ranks change
        this.$nextTick(() => {
          this.updateDateScrollDimensions();
        });
      },
      deep: true,
    },
    stores: {
      handler() {
        if (this.stores.length > 0 && this.selectedWIDs.length === 0) {
          // If we have stores but no selections, emit to select the first one
          const firstLocationId = this.stores[0].gmbProfile.name.split("/")[1];
          this.$emit("update:selectedWIDs", [firstLocationId]);
          this.loadLocationData();
        }
      },
      immediate: true,
    },
    selectedWIDs: {
      immediate: true,
      handler(newVal) {
        this.localSelectedWIDs = [...newVal];
      },
    },
    // Add watcher for distinctKeywordCounts and locationRanksPerformance
    distinctKeywordCounts: {
      handler() {
        this.updateLocalKeywordData();
      },
      deep: true,
    },
    locationRanksPerformance: {
      handler() {
        this.updateLocalKeywordData();
      },
      deep: true,
    },
    selectedDateIndex: {
      handler() {
        this.updateLocalKeywordData();
      },
    },
    availableDates: {
      handler() {
        // When available dates change, update scroll dimensions and scroll to latest
        this.$nextTick(() => {
          this.updateDateScrollDimensions();
        });
      },
      deep: true,
    },
  },
  created() {
    this.selectedDateIndex = this.lastDateIndex;
    if (this.selectedWIDs?.length > 0) {
      this.selectedStores = this.selectedWIDs;
    }
    if (this.locationRanks.length > 0) {
      // reverse the locationRanks array first and then get the date
      const reversedLocationRanks = [...this.locationRanks].reverse();
      this.selectedDate = reversedLocationRanks[this.lastDateIndex].date;
    }
  },
  methods: {
    async fetchWorkspaceData() {
      if (!this.$store.getters.WID || !this.$store.getters.currentUID) {
        console.warn("Cannot fetch workspace data: missing WID or currentUID");
        return;
      }

      try {
        const workspaceRef = doc(
          db,
          "users",
          this.$store.getters.currentUID,
          "fly_workspaces",
          this.$store.getters.WID
        );
        const workspaceDoc = await getDoc(workspaceRef);

        if (workspaceDoc.exists()) {
          this.workspaceData = workspaceDoc.data();
        } else {
          console.warn(
            "Workspace not found in Firestore for WID:",
            this.$store.getters.WID
          );
        }
      } catch (error) {
        console.error("Error fetching workspace data:", error);
      }
    },
    calculateTotalKeywords() {
      // Use distinct_keyword_counts for the selected date if available
      if (
        this.distinctKeywordCounts &&
        Object.keys(this.distinctKeywordCounts).length > 0
      ) {
        const selectedAvailableDateEntry =
          this.availableDates[this.selectedDateIndex];
        if (selectedAvailableDateEntry) {
          const selectedDate = selectedAvailableDateEntry.date;
          const keywordCount = this.distinctKeywordCounts[selectedDate];
          if (keywordCount !== undefined) {
            return keywordCount;
          }
        }
      }
      // Fallback to the original calculation method if distinct_keyword_counts not available
      const uniqueKeywords = new Set();

      this.displayedWorkspaces.forEach((workspace) => {
        if (!workspace.keywords) return;

        workspace.keywords.forEach((keyword) => {
          uniqueKeywords.add(keyword.keyword);
        });
      });

      return uniqueKeywords.size;
    },
    getRankLabel(index) {
      const labels = [
        "Rank 1-3",
        "Rank 4-5",
        "Rank 6-10",
        "Rank 11-20",
        "Rank 21+",
      ];
      return labels[index] || `Rank ${index}`;
    },
    getRankColor(index) {
      const colors = ["#10B981", "#34D399", "#60A5FA", "#F59E0B", "#EF4444"];
      return colors[index] || "#CBD5E1";
    },
    getRankClass(rank) {
      if (rank >= 1 && rank <= 3) return "bg-green-100 text-green-800";
      if (rank >= 4 && rank <= 5) return "bg-green-50 text-green-700";
      if (rank >= 6 && rank <= 10) return "bg-blue-50 text-blue-700";
      if (rank >= 11 && rank <= 20) return "bg-yellow-50 text-yellow-700";
      return "bg-red-50 text-red-700";
    },
    formatRank(rank) {
      if (rank > 20) return "21+";
      return Math.round(rank);
    },
    sortByRank() {
      this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
    },
    selectDate(date, index) {
      this.selectedDateIndex = index;
      this.selectedDate = date;
      // emit the selected date to the parent component
      this.$emit("selectedDate", date);
      // Reset expanded locations when changing dates
      this.expandedLocations = {};
      this.calculateKeywordRanks();
    },
    scrollDates(direction) {
      const scrollAmount = 100; // Amount to scroll in pixels

      if (direction === "left") {
        this.scrollPosition = Math.max(0, this.scrollPosition - scrollAmount);
      } else {
        const maxScroll = Math.max(
          0,
          this.totalDatesWidth - this.dateContainerWidth
        );
        this.scrollPosition = Math.min(
          maxScroll,
          this.scrollPosition + scrollAmount
        );
      }
    },
    scrollDates2(direction) {
      const scrollAmount = 100; // Amount to scroll in pixels

      if (direction === "left") {
        this.scrollPosition2 = Math.max(0, this.scrollPosition2 - scrollAmount);
      } else {
        const maxScroll = Math.max(
          0,
          this.totalDatesWidth2 - this.dateContainerWidth2
        );
        this.scrollPosition2 = Math.min(
          maxScroll,
          this.scrollPosition2 + scrollAmount
        );
      }
    },
    updateDateScrollDimensions() {
      this.$nextTick(() => {
        // First date navigation
        const container = document.querySelector(".dates-container");
        const scrollContent = document.querySelector(".dates-scroll");

        if (container && scrollContent) {
          this.dateContainerWidth = container.offsetWidth;
          this.totalDatesWidth = scrollContent.scrollWidth;
          // Auto-scroll to show latest dates by default
          this.scrollToLatestDates();
        }

        // Second date navigation
        const container2 = document.querySelector(".dates-container-2");
        const scrollContent2 = document.querySelector(".dates-scroll-2");

        if (container2 && scrollContent2) {
          this.dateContainerWidth2 = container2.offsetWidth;
          this.totalDatesWidth2 = scrollContent2.scrollWidth;
          // Auto-scroll to show latest dates by default
          this.scrollToLatestDates2();
        }
      });
    },
    scrollToLatestDates() {
      this.$nextTick(() => {
        // Calculate the maximum scroll position to show the latest dates
        const maxScroll = Math.max(
          0,
          this.totalDatesWidth - this.dateContainerWidth
        );
        this.scrollPosition = maxScroll;
      });
    },
    scrollToLatestDates2() {
      this.$nextTick(() => {
        // Calculate the maximum scroll position to show the latest dates for second navigation
        const maxScroll = Math.max(
          0,
          this.totalDatesWidth2 - this.dateContainerWidth2
        );
        this.scrollPosition2 = maxScroll;
      });
    },
    formatDisplayDate(dateString) {
      if (!dateString) return "Latest Data";

      // Try to format the date if it's a valid date string
      try {
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          });
        }
      } catch (e) {
        // Not a valid date format, return as is
      }

      return dateString;
    },
    navigateDate(direction) {
      if (
        direction === "prev" &&
        this.selectedDateIndex < this.availableDates.length - 1
      ) {
        this.selectedDateIndex++;
      } else if (direction === "next" && this.selectedDateIndex > 0) {
        this.selectedDateIndex--;
      }

      // Reset expanded locations when changing dates
      this.expandedLocations = {};
    },
    toggleStoreDropdown(event) {
      if (event) event.stopPropagation();
      this.showStoreDropdown = !this.showStoreDropdown;
    },
    handleLocationChange(selectedIds) {
      this.localSelectedWIDs = selectedIds;
      let query = { ...this.$route.query };
      if (this.localSelectedWIDs.length > 0) {
        query.location = this.localSelectedWIDs.join(",");
      } else {
        delete query.location;
      }
      this.$router.replace({ query });
      this.$emit("update:selectedWIDs", selectedIds);
      console.log(this.localSelectedWIDs, "these are selected WIDS");
      this.loadLocationData();
    },
    handleClickOutside(event) {
      const dropdown = this.$refs.locationDropdown?.$refs.showStoreDropdown;
      const input = event.target.closest(".location-dropdown");
      if (!input && dropdown && !dropdown.contains(event.target)) {
        this.showStoreDropdown = false;
      }
    },
    loadLocationData() {
      if (this.localSelectedWIDs.length === 0 && this.stores.length > 0) {
        // If no selections but stores available, select all
        const allWids = this.stores.map((store) => store.wid);
        this.localSelectedWIDs = allWids;
        this.$emit("update:selectedWIDs", allWids);
      }

      // Reset expandedLocations when locations change
      this.expandedLocations = {};

      // Process keyword data for stats
      this.processRankData();

      // Calculate keyword ranks independently of monthly data
      this.calculateKeywordRanks();
      this.updateKeywordFilterOptions();
    },
    resetData() {
      this.keywordsData = [];
      this.overallStats = { avgRank: 0, rankChange: null };
      this.topKeyword = {};
      this.availableMonths = [];
      this.selectedMonth = null;
    },
    processRankData() {
      this.isLoading = true;
      if (this.localSelectedWIDs.length === 0 || !this.selectedMonth) {
        this.resetData();
        this.isLoading = false;
        return;
      }

      // Aggregate data from all selected locations
      const allKeywordAggregates = {};
      let locationsWithData = 0;

      for (const locationId of this.localSelectedWIDs) {
        const locationRank = this.locationRanks.find(
          (rank) => rank.locationId === locationId
        );
        if (!locationRank || !locationRank.ranks[this.selectedMonth]) continue;

        const monthData = locationRank.ranks[this.selectedMonth];
        const dates = Object.keys(monthData).filter(
          (key) => key !== "created_at"
        );

        if (dates.length === 0) continue;

        locationsWithData++;

        dates.forEach((date) => {
          const dailyData = monthData[date];
          if (!dailyData || !dailyData.keyword_ranks) return;

          Object.entries(dailyData.keyword_ranks).forEach(
            ([keyword, rankData]) => {
              if (!allKeywordAggregates[keyword]) {
                allKeywordAggregates[keyword] = {
                  totalRank: 0,
                  count: 0,
                  locations: new Set(),
                };
              }
              allKeywordAggregates[keyword].totalRank += rankData.average_rank;
              allKeywordAggregates[keyword].count += 1;
              allKeywordAggregates[keyword].locations.add(locationId);
            }
          );
        });
      }

      if (locationsWithData === 0) {
        this.resetData();
        this.isLoading = false;
        return;
      }

      this.keywordsData = Object.entries(allKeywordAggregates).map(
        ([keyword, agg]) => {
          const avgRank = agg.totalRank / agg.count;
          return {
            keyword,
            avgRank,
            change: null,
            locationCount: agg.locations.size,
          };
        }
      );

      this.keywordsData.sort((a, b) => a.avgRank - b.avgRank);

      if (this.keywordsData.length > 0) {
        this.overallStats.avgRank =
          this.keywordsData.reduce((sum, kw) => sum + kw.avgRank, 0) /
          this.keywordsData.length;
        this.topKeyword = this.keywordsData[0];

        const previousMonth = this.getPreviousMonth(this.selectedMonth);
        if (previousMonth && this.availableMonths.includes(previousMonth)) {
          const prevKeywordAggregates = {};

          for (const locationId of this.localSelectedWIDs) {
            const locationRank = this.locationRanks.find(
              (rank) => rank.locationId === locationId
            );
            if (!locationRank || !locationRank.ranks[previousMonth]) continue;

            const prevMonthData = locationRank.ranks[previousMonth];
            const prevDates = Object.keys(prevMonthData).filter(
              (key) => key !== "created_at"
            );

            prevDates.forEach((date) => {
              const dailyData = prevMonthData[date];
              if (!dailyData || !dailyData.keyword_ranks) return;

              Object.entries(dailyData.keyword_ranks).forEach(
                ([keyword, rankData]) => {
                  if (!prevKeywordAggregates[keyword]) {
                    prevKeywordAggregates[keyword] = {
                      totalRank: 0,
                      count: 0,
                    };
                  }
                  prevKeywordAggregates[keyword].totalRank +=
                    rankData.average_rank;
                  prevKeywordAggregates[keyword].count += 1;
                }
              );
            });
          }

          const prevAvgRanks = {};
          Object.entries(prevKeywordAggregates).forEach(([keyword, agg]) => {
            prevAvgRanks[keyword] = agg.totalRank / agg.count;
          });

          this.keywordsData.forEach((kw) => {
            if (prevAvgRanks[kw.keyword]) {
              kw.change = prevAvgRanks[kw.keyword] - kw.avgRank;
            }
          });

          const commonKeywords = this.keywordsData.filter(
            (kw) => prevAvgRanks[kw.keyword]
          );
          if (commonKeywords.length > 0) {
            const currentAvg =
              commonKeywords.reduce((sum, kw) => sum + kw.avgRank, 0) /
              commonKeywords.length;
            const prevAvg =
              commonKeywords.reduce(
                (sum, kw) => sum + prevAvgRanks[kw.keyword],
                0
              ) / commonKeywords.length;
            this.overallStats.rankChange = prevAvg - currentAvg;
          }
        }
      }

      // Calculate keyword performance data
      this.calculateKeywordRanks();

      // Add available keywords to the filter options
      this.updateKeywordFilterOptions();

      this.isLoading = false;
    },
    async handleScreenshotCapture() {
      try {
        // Show header for screenshot
        this.showScreenshotHeader = true;

        // Wait for DOM update
        await this.$nextTick();

        const canvas = await html2canvas(
          document.querySelector(".dashboard-container"),
          {
            scale: 2,
            useCORS: true,
            logging: false,
            backgroundColor: "#ffffff",
          }
        );

        // Hide header after screenshot
        this.showScreenshotHeader = false;

        const link = document.createElement("a");
        const date = new Date();
        const formattedDate = date.toISOString().split("T")[0];
        link.download = `${this.currentWorkspaceName}-competitor-dashboard-${formattedDate}.png`;
        link.href = canvas.toDataURL("image/png");
        link.click();

        this.$snackbar?.show("Screenshot saved");
      } catch (error) {
        console.error("Error capturing screenshot:", error);
        this.showScreenshotHeader = false; // Ensure header is hidden on error
        alert("Screenshot feature encountered an error");
      }
    },
    getPreviousMonth(currentMonth) {
      const [month, year] = currentMonth.split("-").map(Number);
      const date = new Date(year, month - 1, 1);
      date.setMonth(date.getMonth() - 1);
      const prevMonth = String(date.getMonth() + 1).padStart(2, "0");
      const prevYear = date.getFullYear();
      return `${prevMonth}-${prevYear}`;
    },
    formatDate(dateString) {
      if (!dateString) return "";

      const [month, year] = dateString.split("-");
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      return `${monthNames[parseInt(month) - 1]} ${year}`;
    },
    calculateOverallRank() {
      if (!this.displayedWorkspaces.length) return "N/A";

      const validRanks = this.displayedWorkspaces
        .map((ws) => ws.average_rank)
        .filter((r) => typeof r === "number");

      if (!validRanks.length) return "N/A";

      return (
        Math.round(
          (validRanks.reduce((a, b) => a + b, 0) / validRanks.length) * 10
        ) / 10
      ).toFixed(1);
    },
    toggleLocationDetails(wid) {
      // Using Vue.set to ensure reactivity
      this.$set(this.expandedLocations, wid, !this.expandedLocations[wid]);
    },
    getLocationName(wid) {
      const store = this.stores.find((s) => s.wid === wid);
      if (store) {
        const primaryTitle =
          store.title || (store.gmbProfile && store.gmbProfile.title);
        const secondaryName = store.display_name || store.name;

        let name = primaryTitle || secondaryName || "Unknown Location";
        if (
          primaryTitle &&
          secondaryName &&
          primaryTitle.toLowerCase() === secondaryName.toLowerCase()
        ) {
          // If title and name are identical, just use one.
          name = primaryTitle;
        } else if (primaryTitle && secondaryName) {
          // If different, you might choose to concatenate or prioritize one.
          // For now, prioritizing primaryTitle if it exists.
          name = primaryTitle;
        }

        let address = "Address not available";
        if (store.gmbProfile && store.gmbProfile.address) {
          address = store.gmbProfile.address;
        } else {
          const parts = [];
          // Ensure address components are strings before trying to join or push
          if (store.sub_locality && typeof store.sub_locality === "string")
            parts.push(store.sub_locality);
          if (store.city && typeof store.city === "string")
            parts.push(store.city);
          if (store.state && typeof store.state === "string")
            parts.push(store.state);
          if (parts.length > 0) {
            address = parts.join(", ");
          }
        }
        return { name, address };
      }
      return { name: `Location (${wid})`, address: "Address not available" };
    },
    navigateToRankExplorer(wid, keyword) {
      // Create URL with the location and/or keyword parameters
      let url = "/local-rank-insights?";
      const params = [];

      if (wid) {
        params.push(`location=${wid}`);
      }

      if (keyword) {
        params.push(`keyword=${encodeURIComponent(keyword)}`);
      }
      if (this.selectedDate) {
        params.push(`date=${this.selectedDate}`);
      }
      params.push(`tab=rank_explorer`);

      url += params.join("&");

      // Open the URL in a new tab
      window.open(url, "_blank");
    },
    updateRankDistribution() {
      // This will be called when the keyword filter changes
      // The rankDistribution computed property will react accordingly
    },
    sortKeywordTable(column) {
      // Toggle sort direction if clicking the same column
      if (this.keywordTableSort.column === column) {
        this.keywordTableSort.direction =
          this.keywordTableSort.direction === "asc" ? "desc" : "asc";
      } else {
        this.keywordTableSort.column = column;
        this.keywordTableSort.direction = "asc";
      }
    },
    calculateKeywordRanks() {
      // Reset the keyword ranks array
      this.performanceKeywordRanks = [];

      // Check if locationRanksPerformance is available
      if (
        !this.locationRanksPerformance ||
        Object.keys(this.locationRanksPerformance).length === 0
      ) {
        console.log("no locationRanksPerformance");
        return;
      }

      // Get the current selected date
      const selectedAvailableDateEntry =
        this.availableDates[this.selectedDateIndex];
      if (!selectedAvailableDateEntry) return;

      const selectedDate = selectedAvailableDateEntry.date;

      // Get performance data for the selected date
      const datePerformanceData = this.locationRanksPerformance[selectedDate];

      if (!datePerformanceData || !datePerformanceData.keywords) return;

      // Use performance data from the backend response
      const keywordMap = new Map();

      // Process keywords directly from the datePerformanceData
      if (Array.isArray(datePerformanceData.keywords)) {
        datePerformanceData.keywords.forEach((keywordEntry) => {
          const keyword = keywordEntry.keyword;
          const count = keywordEntry.count;
          const averageRank = keywordEntry.average_rank;

          if (!keywordMap.has(keyword)) {
            keywordMap.set(keyword, {
              keyword,
              totalCount: 0,
              occurrences: 0,
              averageRank: 0,
            });
          }

          const data = keywordMap.get(keyword);
          data.totalCount += count;
          data.occurrences += 1;
          data.averageRank = averageRank;
        });
      }

      // Convert the map to an array and calculate performance metrics
      this.performanceKeywordRanks = Array.from(keywordMap.values()).map(
        (data) => ({
          keyword: data.keyword,
          avgRank: data.averageRank, // Average count
          count: data.totalCount, // Total count
        })
      );

      // Sort by total count (highest performing keywords first)
      this.performanceKeywordRanks.sort((a, b) => b.avgRank - a.avgRank);

      // Update local keyword data and filter options
      this.updateLocalKeywordData();
    },
    updateLocalKeywordData() {
      // Copy keywords data to local variable
      if (
        this.performanceKeywordRanks &&
        this.performanceKeywordRanks.length > 0
      ) {
        this.localKeywordData = [...this.performanceKeywordRanks];
      } else {
        // Fallback to extract keywords from current displayed workspaces
        const keywordMap = new Map();

        this.displayedWorkspaces.forEach((workspace) => {
          if (!workspace.keywords) return;

          workspace.keywords.forEach((keyword) => {
            if (keywordMap.has(keyword.keyword)) {
              const existing = keywordMap.get(keyword.keyword);
              existing.count += 1;
            } else {
              keywordMap.set(keyword.keyword, {
                keyword: keyword.keyword,
                count: 1,
                avgRank: keyword.rank || 0,
              });
            }
          });
        });

        this.localKeywordData = Array.from(keywordMap.values());
      }

      // Update keyword filter options
      this.updateKeywordFilterOptions();
    },
    updateKeywordFilterOptions() {
      // Start with the "All Keywords" option
      const options = [{ text: "All Keywords", value: "all" }];

      // Add an option for each keyword with count
      this.localKeywordData.forEach((data) => {
        options.push({
          text: `${data.keyword}`,
          value: data.keyword,
        });
      });

      this.keywordFilterOptions = options;
    },
    showRawData(data) {
      this.rawDetailsData = JSON.stringify(data, null, 2);
    },
  },
};
</script>

<style scoped>
/* Screenshot Header Styles */
.screenshot-header {
  margin: -1rem -1rem 1.5rem -1rem; /* Negative margins to extend to container edges */
  position: relative;
  overflow: hidden;
}

.screenshot-header.hidden {
  display: none;
}

.maze-pattern {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  position: relative;
  padding: 1.5rem 2rem;
  overflow: hidden;
}

.maze-pattern::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    /* Horizontal lines */ linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.1) 1px,
      transparent 1px
    ),
    /* Vertical lines */
      linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px),
    /* Diagonal cross pattern */
      linear-gradient(
        45deg,
        transparent 35%,
        rgba(255, 255, 255, 0.05) 35%,
        rgba(255, 255, 255, 0.05) 65%,
        transparent 65%
      ),
    /* Plus symbols */
      radial-gradient(
        circle at 25% 25%,
        rgba(255, 255, 255, 0.1) 2px,
        transparent 3px
      ),
    radial-gradient(
      circle at 75% 75%,
      rgba(255, 255, 255, 0.08) 2px,
      transparent 3px
    );
  background-size: 20px 20px, 20px 20px, 40px 40px, 80px 80px, 120px 120px;
  animation: mazeFloat 20s linear infinite;
}

@keyframes mazeFloat {
  0% {
    transform: translateX(0px);
  }
  100% {
    transform: translateX(40px);
  }
}

.header-content {
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.workspace-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.workspace-logo {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  object-fit: cover;
  border: 2px solid rgba(255, 255, 255, 0.2);
}

.workspace-name {
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  transform: translateY(-10px);
}

.date-text {
  color: rgba(255, 255, 255, 0.8);
  font-size: 1rem;
  font-weight: 500;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  position: relative;
  padding-right: 4rem;
  transform: translateY(-10px);
}

.date-text::after {
  content: "";
  position: absolute;
  right: -3.5rem;
  top: -20px;
  width: 60px;
  height: 80px;
  background-image: 
    /* Row 1 - Top bubbles */ radial-gradient(
      circle at 10px 8px,
      rgba(255, 255, 255, 0.4) 2px,
      transparent 3px
    ),
    radial-gradient(
      circle at 25px 5px,
      rgba(255, 255, 255, 0.3) 1.5px,
      transparent 2px
    ),
    radial-gradient(
      circle at 40px 10px,
      rgba(255, 255, 255, 0.35) 2.5px,
      transparent 3px
    ),
    radial-gradient(
      circle at 55px 7px,
      rgba(255, 255, 255, 0.25) 1px,
      transparent 2px
    ),
    /* Row 2 */
      radial-gradient(
        circle at 8px 20px,
        rgba(255, 255, 255, 0.3) 1.5px,
        transparent 2px
      ),
    radial-gradient(
      circle at 22px 18px,
      rgba(255, 255, 255, 0.4) 2px,
      transparent 3px
    ),
    radial-gradient(
      circle at 35px 22px,
      rgba(255, 255, 255, 0.2) 1px,
      transparent 2px
    ),
    radial-gradient(
      circle at 48px 19px,
      rgba(255, 255, 255, 0.35) 2px,
      transparent 3px
    ),
    /* Row 3 - Middle */
      radial-gradient(
        circle at 12px 32px,
        rgba(255, 255, 255, 0.25) 2px,
        transparent 3px
      ),
    radial-gradient(
      circle at 28px 30px,
      rgba(255, 255, 255, 0.2) 1.5px,
      transparent 2px
    ),
    radial-gradient(
      circle at 42px 34px,
      rgba(255, 255, 255, 0.3) 2.5px,
      transparent 3px
    ),
    radial-gradient(
      circle at 52px 31px,
      rgba(255, 255, 255, 0.15) 1px,
      transparent 2px
    ),
    /* Row 4 */
      radial-gradient(
        circle at 6px 44px,
        rgba(255, 255, 255, 0.2) 1px,
        transparent 2px
      ),
    radial-gradient(
      circle at 20px 42px,
      rgba(255, 255, 255, 0.25) 1.5px,
      transparent 2px
    ),
    radial-gradient(
      circle at 36px 46px,
      rgba(255, 255, 255, 0.15) 2px,
      transparent 3px
    ),
    radial-gradient(
      circle at 50px 43px,
      rgba(255, 255, 255, 0.2) 1.5px,
      transparent 2px
    ),
    /* Row 5 - Bottom */
      radial-gradient(
        circle at 15px 56px,
        rgba(255, 255, 255, 0.15) 1px,
        transparent 2px
      ),
    radial-gradient(
      circle at 30px 54px,
      rgba(255, 255, 255, 0.1) 1.5px,
      transparent 2px
    ),
    radial-gradient(
      circle at 45px 58px,
      rgba(255, 255, 255, 0.12) 1px,
      transparent 2px
    ),
    /* Additional scattered bubbles */
      radial-gradient(
        circle at 18px 68px,
        rgba(255, 255, 255, 0.08) 1px,
        transparent 2px
      ),
    radial-gradient(
      circle at 38px 70px,
      rgba(255, 255, 255, 0.06) 1px,
      transparent 2px
    );
  background-size: 60px 80px;
}

.date-text::before {
  content: "";
  position: absolute;
  right: -3rem;
  top: -15px;
  width: 50px;
  height: 70px;
  background-image: 
    /* Left side additional bubbles */ radial-gradient(
      circle at 5px 12px,
      rgba(255, 255, 255, 0.2) 1px,
      transparent 2px
    ),
    radial-gradient(
      circle at 15px 8px,
      rgba(255, 255, 255, 0.15) 1.5px,
      transparent 2px
    ),
    radial-gradient(
      circle at 8px 25px,
      rgba(255, 255, 255, 0.18) 1px,
      transparent 2px
    ),
    radial-gradient(
      circle at 18px 28px,
      rgba(255, 255, 255, 0.12) 1px,
      transparent 2px
    ),
    radial-gradient(
      circle at 12px 40px,
      rgba(255, 255, 255, 0.1) 1px,
      transparent 2px
    ),
    radial-gradient(
      circle at 22px 45px,
      rgba(255, 255, 255, 0.08) 1px,
      transparent 2px
    ),
    radial-gradient(
      circle at 6px 55px,
      rgba(255, 255, 255, 0.06) 1px,
      transparent 2px
    ),
    radial-gradient(
      circle at 16px 60px,
      rgba(255, 255, 255, 0.05) 1px,
      transparent 2px
    );
  background-size: 50px 70px;
}

/* Existing styles */
.rank-distribution {
  margin: 2rem 1rem;
  padding-bottom: 2rem;
}

.rank-bar {
  border-radius: 4px;
  min-width: 40px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.grid-ranks {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
}

.grid-rank-bubble {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  color: white;
}

.v-data-table thead th {
  position: sticky;
  top: 0;
  background-color: white;
  z-index: 1;
}

.v-data-table tbody tr:hover {
  background-color: #f5f5f5;
}

:root {
  --brand-success: #28a745;
  --brand-danger: #dc3545;
}
.text-brand-success {
  color: var(--brand-success);
}
.text-brand-danger {
  color: var(--brand-danger);
}

/* Add styles for location dropdown */
.location-dropdown {
  position: relative;
}
.keyword-table {
  table-layout: fixed;
}

.keyword-table th:first-child,
.keyword-table td:first-child {
  width: 88%;
  text-align: left;
  padding-right: 8px;
  word-break: break-word;
}

.keyword-table th:last-child,
.keyword-table td:last-child {
  width: 22%;
  text-align: center;
}

.keyword-table th,
.keyword-table td {
  text-align: left; /* Ensure text is left-aligned in keyword tables */
}
</style>
